import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchAgencies, fetchUsers } from "../services/api"; // Removed createAgency
import "./AdminDashboard.css";
import axios from "axios";

// Import components for tabs
import AdminBookingsManagement from "../components/admin/AdminBookingsManagement";
import UsersManagement from "../components/admin/UsersManagement";
import AgentAdminsManagement from "../components/admin/AgentAdminsManagement";
import AgenciesManagement from "../components/admin/AgenciesManagement";

const AdminDashboard = () => {
  const { currentUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabsVisible, setTabsVisible] = useState(false);

  // Function to fetch dashboard data
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch agencies data
      let agenciesData = [];
      try {
        agenciesData = await fetchAgencies();
        console.log("Agencies data:", agenciesData);
      } catch (err) {
        console.error("Error fetching agencies:", err);
        agenciesData = []; // Set empty array as fallback
      }
      setAgencies(agenciesData);

      // Fetch users data
      let usersData = [];
      try {
        usersData = await fetchUsers();
        // Ensure users is an array
        usersData = Array.isArray(usersData) ? usersData : [];
      } catch (err) {
        console.error("Error fetching users:", err);
        usersData = []; // Set empty array as fallback
      }
      console.log("Users data:", usersData);
      setUsers(usersData);

      // Fetch booking requests data
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        };
        
        const timestamp = new Date().getTime();
        const bookingsResponse = await axios.get(`/api/bookings/admin?_t=${timestamp}`, config);
        
        if (bookingsResponse.data && Array.isArray(bookingsResponse.data)) {
          setBookingRequests(bookingsResponse.data);
        }
      } catch (err) {
        console.error("Error fetching booking requests:", err);
        setBookingRequests([]); // Set empty array as fallback
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching admin dashboard data:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to load dashboard data. Please try again later.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Redirect if not super admin
    if (!isSuperAdmin()) {
      navigate("/dashboard");
      return;
    }

    // Load data
    fetchData();
  }, [navigate, isSuperAdmin]);

  // Get agent admins from users array safely
  const getAgentAdmins = () => {
    if (!Array.isArray(users)) return [];
    return users.filter((user) => user && user.role === "agent_admin");
  };

  // Get regular users count safely
  const getRegularUsersCount = () => {
    if (!Array.isArray(users)) return 0;
    return users.filter((user) => user && user.role === "user").length;
  };

  // Get pending booking requests count
  const getPendingBookingRequests = () => {
    if (!Array.isArray(bookingRequests)) return 0;
    return bookingRequests.filter((booking) => booking && booking.status === "pending_admin").length;
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTabsVisible(false); // Hide tabs after selection on mobile
  };

  // Toggle tabs visibility for mobile
  const toggleTabs = () => {
    setTabsVisible(!tabsVisible);
  };

  // Render the dashboard overview
  const renderOverview = () => {
    return (
      <div className="dashboard-overview">
        <div className="stats-grid">
          <div className="stat-card">
            <h4>Total Agencies</h4>
            <p className="stat-number">{Array.isArray(agencies) ? agencies.length : 0}</p>
            <div className="stat-icon">
              <i className="fas fa-building"></i>
            </div>
          </div>
          <div className="stat-card">
            <h4>Total Users</h4>
            <p className="stat-number">{Array.isArray(users) ? users.length : 0}</p>
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
          </div>
          <div className="stat-card">
            <h4>Agent Admins</h4>
            <p className="stat-number">{getAgentAdmins().length}</p>
            <div className="stat-icon">
              <i className="fas fa-user-tie"></i>
            </div>
          </div>
          <div className="stat-card">
            <h4>Booking Requests</h4>
            <p className="stat-number">
              {Array.isArray(bookingRequests) ? bookingRequests.length : 0}
            </p>
            <div className="stat-icon">
              <i className="fas fa-clipboard-list"></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render different tabs based on activeTab state
  const renderTabContent = () => {
    switch (activeTab) {
      case "agencies":
        return <AgenciesManagement />;
      case "users":
        return <UsersManagement users={users} updateUsers={setUsers} />;
      case "agent-admins":
        return <AgentAdminsManagement />;
      case "bookings":
        return <AdminBookingsManagement />;
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-content-fullwidth">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-content-fullwidth">
          <div className="error-container">
            <h2>Error</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={fetchData}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-content-fullwidth">
        <div className="admin-header">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="welcome-message">
              Welcome back, {currentUser?.name || "Admin"}
            </p>
          </div>
        </div>

        {/* Mobile tabs toggle button */}
        <button className="tabs-toggle" onClick={toggleTabs}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
          </svg>
          {activeTab === "overview" ? "Dashboard Menu" : `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`}
        </button>

        <div className={`admin-tabs ${tabsVisible ? 'show' : ''}`}>
          <button
            className={activeTab === "overview" ? "active" : ""}
            onClick={() => handleTabChange("overview")}
          >
            Overview
          </button>
          <button
            className={activeTab === "agencies" ? "active" : ""}
            onClick={() => handleTabChange("agencies")}
          >
            Agencies
          </button>
          <button
            className={activeTab === "users" ? "active" : ""}
            onClick={() => handleTabChange("users")}
          >
            Users
          </button>
          <button
            className={activeTab === "agent-admins" ? "active" : ""}
            onClick={() => handleTabChange("agent-admins")}
          >
            Agent Admins
          </button>
          <button
            className={activeTab === "bookings" ? "active" : ""}
            onClick={() => handleTabChange("bookings")}
          >
            Booking Requests
          </button>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminDashboard;
